/**
 * Global: Mint
 * ------------------------------------------------------------------------------
 * Main entry point for global components and helpers for the storefront.
 */
import '@/global/mint.scss'

import CustomerFormListener from '~/components/customer/form-listener'
import LocalizedTime from '~/components/localized-time' /* registered in component itself */

import { getProduct } from '~/core/get-product'
import { getString } from '~/core/strings'
import { log } from '~/core/log-error'
import EventBus from '~/core/event-bus'
import Mint from '~/core/mint/mint'
import Query from '~/core/query'
import Sentry from '~/core/sentry'

import { isRobot, setCollectionScrollRestoreState } from '~/helpers/dom'
import { detectKeyboardNavigation } from '~/helpers/accessibility'
import AttraqtTracking from '~/helpers/attraqt-tracking'
import Bloomreach from '~/helpers/bloomreach'
import Cart from '~/helpers/cart'
import Customer from '~/helpers/customer'
import Wishlist from '~/helpers/wishlist'

import Swym from '~/api/swym'

window.mint = window.mint || {}
window.mint.EventBus = EventBus()
window.mint.GetProduct = ({ handle, id, flexi }) => getProduct({ handle, id, flexi })
window.mint.Log = (level, message, error, additionalData) => log(level, message, error, additionalData)
window.mint.Query = (query) => Query(query)
window.mint.Strings = (string, replace) => getString(string, replace)

if (mint.flags.sentry.enable) {
  Sentry().init()
}

/**
 * DOM is ready.
 */
document.addEventListener('DOMContentLoaded', () => {
  if (mint.flags.collections.enableRestoreScroll) {
    setCollectionScrollRestoreState()
  }

  detectKeyboardNavigation()

  /**
   * Global methods - not bound to a node.
   */
  Cart().init()
  Wishlist().init()

  /**
   * Mint Framework.
   * - Initiate after global events to sync EventBus.
   */
  Mint().init()

  if (!mint.customer.loggedIn) {
    CustomerFormListener().init()
  }

  /**
   * Run Attraqt & Bloomreach tracking scripts at very end of page load.
   * - Keep inside `DOMContentLoaded` (not `load`) to ensure other
   *   components can still dispatch events.
   */
  if (
    mint.services.attraqt.tracking.enabled &&
    mint.services.attraqt.tracking.key
  ) {
    AttraqtTracking().init()
  }

  if (mint.services.bloomreach) {
    Bloomreach().init()
  }

  /**
   * Initialise customer events at the end, so any helper listening
   * out for the customer:stateUpdated` event doesn't initialise before
   * the event sends.
   */
  Customer().init()

  /**
   * Added for Google Bot debugging.
   */
  if (isRobot()) {
    document.body.setAttribute('data-is-search-robot', true)
  }

  Swym().init()

  /**
   * Developer mode.
   * - Only import if Webpack is in development mode.
   */
  if (mint.env.mode === 'development') {
    import(
      /* webpackChunkName: 'component.developer-mode' */
      '~/helpers/developer-mode'
    ).then((module) => {
      const DeveloperMode = module.default

      DeveloperMode().init()
    })
  }
})
